import React, { createContext, useContext, useReducer, useEffect } from 'react';
import supabase from '../lib/supabase';
import api from '../services/api';

const AuthContext = createContext();

const initialState = {
  user: null,
  profile: null,
  session: null,
  loading: true,
  error: null
};

function authReducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_USER':
      return { ...state, user: action.payload, loading: false };
    case 'SET_PROFILE':
      return { ...state, profile: action.payload };
    case 'SET_SESSION':
      return { ...state, session: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SIGN_OUT':
      return { ...initialState, loading: false };
    default:
      return state;
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Session error:', error);
          throw error;
        }
        
        if (session?.access_token) {
          // Validate session is not expired
          const currentTime = Date.now() / 1000;
          if (session.expires_at && currentTime > session.expires_at) {
            console.log('Session expired, refreshing...');
            const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
            if (refreshError) throw refreshError;
            if (refreshedSession) {
              dispatch({ type: 'SET_SESSION', payload: refreshedSession });
              await fetchUserProfile(refreshedSession.access_token);
              return;
            }
          }
          
          dispatch({ type: 'SET_SESSION', payload: session });
          await fetchUserProfile(session.access_token);
        } else {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } catch (error) {
        console.error('Error getting session:', error);
        dispatch({ type: 'SET_ERROR', payload: error.message });
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          dispatch({ type: 'SET_SESSION', payload: session });
          await fetchUserProfile(session.access_token);
        } else if (event === 'SIGNED_OUT') {
          dispatch({ type: 'SIGN_OUT' });
        }
      }
    );

    // Handle page visibility changes to refresh session
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        // Check if session needs refresh when user returns to tab
        const session = await checkSession();
        if (!session) {
          // Session expired while away, sign out
          dispatch({ type: 'SIGN_OUT' });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchUserProfile = async (token) => {
    try {
      const response = await api.get('/profiles/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      dispatch({ type: 'SET_USER', payload: response.data.user });
      dispatch({ type: 'SET_PROFILE', payload: response.data.profile });
    } catch (error) {
      console.error('Error fetching user profile:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  const signUp = async (email, password, userType, languagePreference = 'en') => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      const response = await api.post('/auth/signup', {
        email,
        password,
        userType,
        languagePreference
      });

      // Ensure we have session data
      if (!response.data.session) {
        throw new Error('No session returned from signup');
      }

      // Set session and user from signup response
      dispatch({ type: 'SET_SESSION', payload: response.data.session });
      dispatch({ type: 'SET_USER', payload: response.data.user });

      // Set the session in Supabase client for immediate authentication
      try {
        const { error: sessionError } = await supabase.auth.setSession(response.data.session);
        if (sessionError) {
          console.error('Session setting error:', sessionError);
          throw sessionError;
        }

        // Also manually store the session as backup
        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
        if (supabaseUrl) {
          const projectRef = supabaseUrl.split('//')[1].split('.')[0];
          const authKey = `sb-${projectRef}-auth-token`;
          localStorage.setItem(authKey, JSON.stringify(response.data.session));
          console.log('Session stored successfully');
        }
      } catch (sessionErr) {
        // Log session error but don't fail the signup - user is still authenticated via API
        console.error('Failed to set Supabase session:', sessionErr);
      }

      return { success: true, user: response.data.user };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: false, error: errorMessage };
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const signIn = async (email, password) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      const response = await api.post('/auth/signin', { email, password });

      // Ensure we have session data
      if (!response.data.session) {
        throw new Error('No session returned from signin');
      }

      dispatch({ type: 'SET_SESSION', payload: response.data.session });
      dispatch({ type: 'SET_USER', payload: response.data.user });

      // Set the session in Supabase client for immediate authentication
      try {
        const { error: sessionError } = await supabase.auth.setSession(response.data.session);
        if (sessionError) {
          console.error('Session setting error:', sessionError);
          throw sessionError;
        }

        // Also manually store the session as backup
        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
        if (supabaseUrl) {
          const projectRef = supabaseUrl.split('//')[1].split('.')[0];
          const authKey = `sb-${projectRef}-auth-token`;
          localStorage.setItem(authKey, JSON.stringify(response.data.session));
          console.log('Session stored successfully');
        }
      } catch (sessionErr) {
        // Log session error but don't fail the signin - user is still authenticated via API
        console.error('Failed to set Supabase session:', sessionErr);
      }

      return { success: true, user: response.data.user };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: false, error: errorMessage };
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const signOut = async () => {
    try {
      await api.post('/auth/signout');
      await supabase.auth.signOut();
      dispatch({ type: 'SIGN_OUT' });
      return { success: true };
    } catch (error) {
      console.error('Error signing out:', error);
      return { success: false, error: error.message };
    }
  };

  const verifyPhone = async (phone, code) => {
    try {
      const response = await api.post('/auth/verify-phone', { phone, code });
      return { success: true, message: response.data.message };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return { success: false, error: errorMessage };
    }
  };

  const resendCode = async (phone) => {
    try {
      const response = await api.post('/auth/resend-code', { phone });
      return { success: true, message: response.data.message };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return { success: false, error: errorMessage };
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const endpoint = state.user?.user_type === 'restaurant' 
        ? '/profiles/restaurant' 
        : '/profiles/worker';

      const formData = new FormData();
      
      Object.keys(profileData).forEach(key => {
        if (profileData[key] !== null && profileData[key] !== undefined) {
          if (typeof profileData[key] === 'object' && !(profileData[key] instanceof File)) {
            formData.append(key, JSON.stringify(profileData[key]));
          } else {
            formData.append(key, profileData[key]);
          }
        }
      });

      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${state.session?.access_token}`
        }
      });

      dispatch({ type: 'SET_PROFILE', payload: response.data.profile });
      
      return { success: true, profile: response.data.profile };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return { success: false, error: errorMessage };
    }
  };

  const checkSession = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      
      if (session?.access_token) {
        dispatch({ type: 'SET_SESSION', payload: session });
        return session;
      }
      return null;
    } catch (error) {
      console.error('Session check failed:', error);
      return null;
    }
  };

  const value = {
    ...state,
    signUp,
    signIn,
    signOut,
    verifyPhone,
    resendCode,
    updateProfile,
    checkSession,
    refreshProfile: () => fetchUserProfile(state.session?.access_token)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthContext, supabase };